import * as _ from 'lodash'
import { alnum, dateKey, display, isEmpty, joinLabels, NAME_SUFFIXES, stripAccents } from './license-prefill.service'
import { PassportPrefill } from 'src/types/scanner'

/**
 * Prefilling and cross-checking the visa wizard from the passport's
 * machine-readable zone (CU-868mrx5v6).
 *
 * Mirrors license-prefill.service.ts: gov-api returns the MRZ fields already
 * in the wizard model's shape, and only when every check digit passed; what is
 * left here is WHICH paths may be written, that a value the customer has typed
 * is never overwritten without their click, that a scan fills the model ONCE
 * (PASSPORT_PREFILL_SOURCE_PATH remembers which scan), and how to compare
 * leniently.
 *
 * Step order (dev forms walked 2026-11-04, all 11 that ask for the scan): the
 * scan step comes right BEFORE the passport-details step, and after the name,
 * gender and date-of-birth steps. So at the scan step the passport fields are
 * blank (filled here) and the legal name / DOB / gender / citizenship are
 * already typed (compared here); the passport fields are typed on the next
 * step, where the stepper compares them once more (passportPathsInStep).
 *
 * What the MRZ is good for, and what it is not:
 *   - document number, date of birth, expiry: each has its own check digit
 *     plus the composite — a mismatch here is almost certainly a typo. These
 *     are the only disagreements ever shown to the customer.
 *   - names, sex, nationality: no check digit; OCR with transliteration (no
 *     diacritics, Ü may be UE or U, no hyphens or apostrophes) — filled into
 *     blanks (reviewed on the form) and compared leniently for the record,
 *     but never called "wrong" and never written over a typed value.
 *
 * Pure functions on the model object so they can be checked without Angular.
 */

/** `type` values of a passport scan document — shared by FOUR field keys, so gate by key. */
export const PASSPORT_SCAN_TYPES: string[] = ['passport_scan', 'mother_passport_scan', 'father_passport_scan']

/** Field keys whose upload is a passport data page (the page with the photo and the zone). */
export const PASSPORT_DATA_PAGE_KEYS: string[] = ['passport_scan', 'mother.passport_scan', 'father.passport_scan']

/** Same `type: 'passport_scan'`, but not a data page: the analyzer finds no zone and that is fine. */
export const PASSPORT_NON_DATA_PAGE_KEYS: string[] = ['passport_scan_back', 'passport_visa_pages']

/** ISO/IEC 7810 ID-3 (passport page): 125 x 88 mm. */
export const PASSPORT_PAGE_ASPECT_RATIO = 125 / 88

export function isPassportScanType(type: string | undefined | null): boolean {
	return !!type && PASSPORT_SCAN_TYPES.includes(type)
}

/** `passport_scan`, `mother.passport_scan`, … (exact or dotted suffix, as gov-api matches). */
export function isPassportDataPageKey(key: string | undefined | null): boolean {
	if (!key) {
		return false
	}
	return PASSPORT_DATA_PAGE_KEYS.some(k => key === k || key.endsWith('.' + k))
}

export function isPassportNonDataPageKey(key: string | undefined | null): boolean {
	if (!key) {
		return false
	}
	return PASSPORT_NON_DATA_PAGE_KEYS.some(k => key === k || key.endsWith('.' + k))
}

/**
 * A document the analyzer reads for its zone and may send back for a retake.
 * A document with no key (rows from before the key was persisted) is treated
 * as a data page: the server analyses it either way and only rejects when IT
 * knows the key, so the worst case is a checklist on a back cover.
 */
export function isPassportDataPage(document: { type?: string | null, key?: string | null } | null | undefined): boolean {
	return !!document && isPassportScanType(document.type) && !isPassportNonDataPageKey(document.key)
}

/** The applicant's own passport page — the only scan the prefill is ever read from. */
export function isApplicantPassportScan(document: { type?: string | null, key?: string | null } | null | undefined): boolean {
	return !!document && isPassportScanType(document.type) && (!document.key || document.key === 'passport_scan')
}

/**
 * Model paths the visa forms ask for that the zone can answer. `citizenship`,
 * `nationality` and `passport.nationality` are the same question asked under
 * three keys by different forms; askedPassportPaths() narrows this list to
 * the keys the current wizard declares, so a form is only ever filled where
 * it asks. `passport.type` only arrives as `regular` (document code exactly
 * `P`), and both passport field definitions offer `regular`.
 * `passport.issuer` (free-text place of issue) and `passport.issue_date` are
 * not in the zone and are never written.
 */
export const PASSPORT_PREFILL_PATHS: string[] = [
	'passport.first_name',
	'passport.middle_name',
	'passport.last_name',
	'passport.number',
	'passport.expiry_date',
	'passport.nationality',
	'passport.issued_at.country',
	'passport.type',
	'date_of_birth',
	'gender',
	'citizenship',
	'nationality',
]

/**
 * Where the model remembers which scan it has been filled from. Profile-level
 * like everything the scan touches, so a second visa for the same traveler
 * does not fill the blanks again; a scan fills the model ONCE (see
 * license-prefill.service.ts for the prod bug this rule comes from).
 */
export const PASSPORT_PREFILL_SOURCE_PATH = 'passport_prefill.document_uuid'

/** The scan this prefill came from, or '' when there is nothing to fill from. */
export function passportPrefillSource(prefill: PassportPrefill | null | undefined): string {
	return (prefill?.available && prefill.source?.document_uuid) || ''
}

/** Has ANY passport scan ever been written into this model? */
export function hasPassportPrefillMarker(model: any): boolean {
	return !!_.get(model, PASSPORT_PREFILL_SOURCE_PATH)
}

/** Has THIS scan been recorded against the model? */
export function passportPrefillApplied(model: any, prefill: PassportPrefill | null | undefined): boolean {
	const source = passportPrefillSource(prefill)
	return !!source && _.get(model, PASSPORT_PREFILL_SOURCE_PATH) === source
}

/** Remember the scan as applied. Returns true when the model changed. */
export function markPassportPrefillApplied(model: any, prefill: PassportPrefill | null | undefined): boolean {
	const source = passportPrefillSource(prefill)
	if (!source || _.get(model, PASSPORT_PREFILL_SOURCE_PATH) === source) {
		return false
	}
	_.set(model, PASSPORT_PREFILL_SOURCE_PATH, source)
	return true
}

/**
 * Write the zone's fields into the model where the customer hasn't typed
 * anything yet. Returns the paths that were filled. Names go in exactly as
 * the zone has them (upper case, no diacritics): the banner tells the
 * customer to check them against the printed page.
 */
export function applyPassportPrefill(model: any, prefill: PassportPrefill, paths: string[] = PASSPORT_PREFILL_PATHS): string[] {
	const applied: string[] = []
	if (!prefill?.available || !prefill.fields) {
		return applied
	}
	for (const path of paths) {
		const value = _.get(prefill.fields, path)
		if (isEmpty(value) || !isEmpty(_.get(model, path))) {
			continue
		}
		_.set(model, path, value)
		applied.push(path)
	}
	return applied
}

export interface PassportMismatch {
	path: string
	label: string
	entered: string
	passport: string
	/** Covered by a check digit (number, dates): a disagreement is a typo, not a transliteration. */
	verified: boolean
}

const lower = (v: any) => display(v).trim().toLowerCase()
const upper = (v: any) => display(v).trim().toUpperCase()

/**
 * ICAO 9303 part 3 transliterations that are NOT a plain accent strip: a
 * German passport writes MÜLLER as MUELLER, a Danish one writes ØSTERGÅRD
 * as OESTERGAARD. Applied before the accent strip so both spellings are
 * tried (some states strip instead of transliterating).
 */
const ICAO_FOLDS: [RegExp, string][] = [
	[/Ä/g, 'AE'], [/Ö/g, 'OE'], [/Ü/g, 'UE'], [/ß/g, 'SS'],
	[/Æ/g, 'AE'], [/Ø/g, 'OE'], [/Å/g, 'AA'], [/Œ/g, 'OE'],
	[/Ð/g, 'D'], [/Þ/g, 'TH'], [/Ł/g, 'L'], [/Ñ/g, 'N'], [/Ĳ/g, 'IJ'],
]

const icaoFold = (v: string): string => {
	let out = v.toUpperCase()
	for (const [from, to] of ICAO_FOLDS) {
		out = out.replace(from, to)
	}
	return out
}

const nameTokens = (v: string): string[] =>
	v.toUpperCase().split(/[^A-Z']+/).map(t => t.replace(/[^A-Z]/g, '')).filter(Boolean)

/**
 * The spellings a typed name may legitimately take in the zone: accents
 * stripped, or transliterated the ICAO way. Letters only, no spaces, hyphens
 * or apostrophes (the zone has none of them).
 */
const nameKeys = (v: any, pick: (tokens: string[]) => string[]): Set<string> => {
	const raw = display(v).trim()
	const keys = new Set<string>()
	if (!raw) {
		return keys
	}
	for (const variant of [stripAccents(raw), stripAccents(icaoFold(raw))]) {
		const joined = pick(nameTokens(variant)).join('')
		if (joined) {
			keys.add(joined)
		}
	}
	return keys
}

/** Surname without a generational suffix ("SMITH JR" → SMITH; "DE LA CRUZ" → DELACRUZ). */
const surnameTokens = (tokens: string[]): string[] => {
	const out = [...tokens]
	while (out.length > 1 && NAME_SUFFIXES.has(out[out.length - 1])) {
		out.pop()
	}
	return out
}

/** First given name only: a typed "Mary Ann" vs the zone's MARY (the rest went to middle_name). */
const firstToken = (tokens: string[]): string[] => tokens.slice(0, 1)

type Comparison = {
	path: string
	label: string
	verified: boolean
	skipFlag?: string
	/** Equal when the normalised strings agree, or (names) when any spelling variant does. */
	same: (entered: any, passport: any) => boolean | null
}

const sameBy = (normalise: (v: any) => string) => (a: any, b: any): boolean | null => {
	const x = normalise(a)
	const y = normalise(b)
	// Unparseable on either side is "can't tell", not "wrong".
	return (!x || !y) ? null : x === y
}

const sameName = (pick: (tokens: string[]) => string[]) => (a: any, b: any): boolean | null => {
	const x = nameKeys(a, pick)
	const y = nameKeys(b, pick)
	if (x.size === 0 || y.size === 0) {
		return null
	}
	for (const key of x) {
		if (y.has(key)) {
			return true
		}
	}
	return false
}

/**
 * Fields worth cross-checking against the zone. `passport.middle_name` is
 * left out: the zone's "middle name" is every given name after the first,
 * which the customer may have typed as an initial or not at all.
 */
const COMPARISONS: Comparison[] = [
	{ path: 'passport.number', label: 'Passport number', verified: true, same: sameBy(alnum) },
	{ path: 'passport.expiry_date', label: 'Passport expiration date', verified: true, same: sameBy(dateKey) },
	{ path: 'date_of_birth', label: 'Date of birth', verified: true, same: sameBy(dateKey) },
	{ path: 'passport.first_name', label: 'First name (passport)', verified: false, skipFlag: 'names_truncated', same: sameName(firstToken) },
	{ path: 'passport.last_name', label: 'Last name (passport)', verified: false, skipFlag: 'names_truncated', same: sameName(surnameTokens) },
	{ path: 'first_name', label: 'First name', verified: false, skipFlag: 'names_truncated', same: sameName(firstToken) },
	{ path: 'last_name', label: 'Last name', verified: false, skipFlag: 'names_truncated', same: sameName(surnameTokens) },
	{ path: 'gender', label: 'Gender', verified: false, same: sameBy(lower) },
	{ path: 'citizenship', label: 'Nationality', verified: false, same: sameBy(upper) },
	{ path: 'nationality', label: 'Nationality', verified: false, same: sameBy(upper) },
	{ path: 'passport.nationality', label: 'Nationality', verified: false, same: sameBy(upper) },
	{ path: 'passport.issued_at.country', label: 'Issuing country', verified: false, same: sameBy(upper) },
]

/** The paths compareWithPassport knows how to compare. */
export const PASSPORT_COMPARE_PATHS: string[] = COMPARISONS.map(c => c.path)

/** Where in `prefill.fields` a model path's counterpart lives: the legal name has none of its own, it is the passport name. */
const zonePathFor = (path: string): string =>
	path === 'first_name' || path === 'last_name' ? 'passport.' + path : path

/**
 * Typed values that disagree with the zone, for the paths given (default:
 * everything comparable). The legal name is compared against the passport
 * name the zone carries — they are the same person's name on every form that
 * asks for both.
 */
export function compareWithPassport(model: any, prefill: PassportPrefill, paths: string[] = PASSPORT_COMPARE_PATHS): PassportMismatch[] {
	if (!prefill?.available || !prefill.fields) {
		return []
	}
	const flags = new Set(prefill.flags ?? [])
	const mismatches: PassportMismatch[] = []
	for (const { path, label, verified, skipFlag, same } of COMPARISONS) {
		if (!paths.includes(path) || (skipFlag && flags.has(skipFlag))) {
			continue
		}
		const entered = _.get(model, path)
		const passport = _.get(prefill.fields, zonePathFor(path))
		if (isEmpty(entered) || isEmpty(passport)) {
			continue
		}
		if (same(entered, passport) !== false) {
			continue
		}
		mismatches.push({ path, label, entered: display(entered), passport: display(passport), verified })
	}
	return mismatches
}

/**
 * Overwrite the mismatched paths with the zone's values (customer's explicit
 * choice). Only check-digit-verified fields: an unverified one (a name) is
 * never written over what the customer typed, whatever the caller passes.
 */
export function applyPassportValues(model: any, prefill: PassportPrefill, mismatches: PassportMismatch[]): string[] {
	const applied: string[] = []
	for (const { path, verified } of mismatches) {
		const value = _.get(prefill?.fields, zonePathFor(path))
		if (!verified || isEmpty(value)) {
			continue
		}
		_.set(model, path, value)
		applied.push(path)
	}
	return applied
}

/**
 * "your passport number, expiration date and nationality" — for the banner.
 */
export function describePassportPrefill(paths: string[]): string {
	const groups: { label: string, match: (p: string) => boolean }[] = [
		{ label: 'name', match: p => ['passport.first_name', 'passport.middle_name', 'passport.last_name', 'first_name', 'last_name'].includes(p) },
		{ label: 'passport number', match: p => p === 'passport.number' },
		{ label: 'expiration date', match: p => p === 'passport.expiry_date' },
		{ label: 'date of birth', match: p => p === 'date_of_birth' },
		{ label: 'gender', match: p => p === 'gender' },
		{ label: 'nationality', match: p => ['citizenship', 'nationality', 'passport.nationality'].includes(p) },
		{ label: 'issuing country', match: p => p === 'passport.issued_at.country' },
		{ label: 'passport type', match: p => p === 'passport.type' },
	]
	return joinLabels(groups.filter(g => paths.some(g.match)).map(g => g.label))
}

/**
 * Every model path a wizard step declares, as the stepper's `props.list`
 * entries describe them: `{key: 'passport.number'}` (dotted), `{key:
 * 'passport', type: 'object', subkeys: [{key: 'number'}]}`, or a nested
 * `fieldGroup` (`{key: 'passport', fieldGroup: [{key: 'number'}]}`).
 */
function pathsInStep(step: any): Set<string> {
	const found = new Set<string>()
	const walk = (node: any, prefix: string): void => {
		if (!node || typeof node !== 'object') {
			return
		}
		if (Array.isArray(node)) {
			node.forEach(child => walk(child, prefix))
			return
		}
		const key = node.key !== undefined && node.key !== null ? String(node.key) : ''
		const path = key ? (prefix ? prefix + '.' + key : key) : prefix
		if (key && Array.isArray(node.subkeys)) {
			node.subkeys.forEach((sub: any) => {
				const subkey = sub && sub.key !== undefined ? String(sub.key) : ''
				if (subkey) {
					found.add(path + '.' + subkey)
				}
			})
		}
		if (Array.isArray(node.fieldGroup)) {
			walk(node.fieldGroup, path)
		} else if (Array.isArray(node.fieldArray?.fieldGroup)) {
			walk(node.fieldArray.fieldGroup, path)
		} else if (key && !Array.isArray(node.subkeys)) {
			found.add(path)
		}
	}
	walk(step?.fieldGroup ?? [], '')
	return found
}

/**
 * The comparable passport paths ONE step types — so the stepper can ask
 * "does this step type any passport field?" and compare only those.
 */
export function passportPathsInStep(step: any): string[] {
	const found = pathsInStep(step)
	return PASSPORT_COMPARE_PATHS.filter(p => found.has(p))
}

/**
 * The fillable passport paths the WHOLE wizard asks for, across its steps —
 * so a form that asks `citizenship` is not also given `nationality`, and a
 * form with no passport-type question never gets `passport.type`. With no
 * step list to hand, everything fillable (profile-level, harmless).
 */
export function askedPassportPaths(steps: any[] | null | undefined): string[] {
	if (!Array.isArray(steps) || steps.length === 0) {
		return PASSPORT_PREFILL_PATHS
	}
	const found = new Set<string>()
	steps.forEach(step => pathsInStep(step).forEach(p => found.add(p)))
	return PASSPORT_PREFILL_PATHS.filter(p => found.has(p))
}
